import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Agent } from "@/features/agents/types";
import type {
  ConnectorCapability,
  ConnectorStatus,
  ConnectorType,
} from "@/features/connectors/types";
import { CONNECTOR_CAPABILITIES } from "@/features/connectors/types";
import { useTranslation } from "@/features/utils/i18n";

/** Marks the types only platform staff can create — same in every language. */
const ADMIN_PREFIX = "(Admin)";

interface ChannelTypeFieldProps {
  type: ConnectorType;
  onChange: (type: ConnectorType) => void;
  /** The type is what the rest of the form is shaped by; edit never moves it. */
  disabled: boolean;
  canUseHeyupNumber: boolean;
  hasTemporaryConnector: boolean;
  isSysadmin: boolean;
  /** The types on offer; absent = all of them (editing a connector). */
  offered?: ConnectorType[];
}

export const ChannelTypeField = ({
  type,
  onChange,
  disabled,
  canUseHeyupNumber,
  hasTemporaryConnector,
  isSysadmin,
  offered,
}: ChannelTypeFieldProps) => {
  const { t } = useTranslation("connectors");

  const description =
    type === "heyup"
      ? t(
          "connectors.form.heyupDescription",
          "Le numéro que vous avez commandé chez nous. C'est HeyUp qui l'exploite : rien à configurer côté opérateur, choisissez simplement le numéro et l'agent qui répond.",
        )
      : type === "twilio"
        ? t("connectors.form.twilioDescription")
        : type === "sip"
          ? t("connectors.form.sipDescription")
          : type === "temporary"
            ? t(
                "connectors.form.temporaryDescription",
                "Testez votre agent sans acheter de numéro : appelez le numéro de démonstration HeyUp depuis un de vos numéros déclarés ci-dessous.",
              )
            : type === "email"
              ? t(
                  "connectors.form.emailDescription",
                  "Votre boîte mail (SMTP et IMAP) : HeyUp lit les messages reçus et y répond comme il le ferait par SMS ou par chat.",
                )
              : t(
                  "connectors.form.webDescription",
                  "Ajoutez un chat « Talk with your assistant » sur votre site via un simple script.",
                );

  const isOffered = (value: ConnectorType) =>
    !offered || offered.includes(value);

  return (
    <div className="space-y-2">
      <Label htmlFor="type">{t("connectors.form.channelType")}</Label>
      <Select
        value={type}
        onValueChange={(value) => onChange(value as ConnectorType)}
        disabled={disabled}
      >
        <SelectTrigger id="type">
          <SelectValue
            placeholder={t("connectors.form.channelTypePlaceholder")}
          />
        </SelectTrigger>
        <SelectContent>
          {canUseHeyupNumber && isOffered("heyup") && (
            <SelectItem value="heyup">
              {t("connectors.types.heyup", "Numéro HeyUp")}
            </SelectItem>
          )}
          {isOffered("sip") && (
            <SelectItem value="sip">
              {t("connectors.types.sip", "SIP")}
            </SelectItem>
          )}
          {isOffered("email") && (
            <SelectItem value="email">
              {t("connectors.types.email", "E-mail (boîte mail)")}
            </SelectItem>
          )}
          {isOffered("web") && (
            <SelectItem value="web">
              {t("connectors.types.web", "Web (chat sur site)")}
            </SelectItem>
          )}
          {/* Platform staff only, and labelled so: a Twilio connector means
              "here are credentials, bill this account", and a demo borrows
              someone else's number. The server refuses both from a tenant
              (SYSADMIN_ONLY_TYPES). The item is still rendered for the row
              being edited, so a tenant's existing Twilio connector shows its
              type instead of an empty field. */}
          {isOffered("twilio") && (isSysadmin || type === "twilio") && (
            <SelectItem value="twilio">
              {ADMIN_PREFIX} {t("connectors.types.twilio")}
            </SelectItem>
          )}
          {isOffered("temporary") &&
            ((isSysadmin && !hasTemporaryConnector) ||
              type === "temporary") && (
              <SelectItem value="temporary">
                {ADMIN_PREFIX}{" "}
                {t("connectors.types.temporary", "Numéro temporaire (démo)")}
              </SelectItem>
            )}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  );
};

interface StatusFieldProps {
  status: ConnectorStatus;
  onChange: (status: ConnectorStatus) => void;
}

export const StatusField = ({ status, onChange }: StatusFieldProps) => {
  const { t } = useTranslation("connectors");

  return (
    <div className="space-y-2">
      <Label htmlFor="status">{t("connectors.form.status")}</Label>
      <Select
        value={status}
        onValueChange={(value) => onChange(value as ConnectorStatus)}
      >
        <SelectTrigger id="status">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="active">
            {t("connectors.status.active")}
          </SelectItem>
          <SelectItem value="inactive">
            {t("connectors.status.inactive")}
          </SelectItem>
          <SelectItem value="error">{t("connectors.status.error")}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

interface AgentFieldProps {
  agentId: string;
  onChange: (agentId: string) => void;
  agents: Agent[];
}

export const AgentField = ({ agentId, onChange, agents }: AgentFieldProps) => {
  const { t } = useTranslation("connectors");

  return (
    <div className="space-y-2">
      <Label htmlFor="agent">{t("connectors.form.agent")}</Label>
      <Select value={agentId} onValueChange={onChange}>
        <SelectTrigger id="agent">
          <SelectValue placeholder={t("connectors.form.agentPlaceholder")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="">{t("connectors.form.defaultAgent")}</SelectItem>
          {agents.map((agent) => (
            <SelectItem key={agent.id} value={agent.id!}>
              {agent.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        {t("connectors.form.agentDescription")}
      </p>
    </div>
  );
};

interface CapabilitiesFieldProps {
  type: ConnectorType;
  capabilities: ConnectorCapability[];
  onToggle: (capability: ConnectorCapability) => void;
}

/** The three rows, each shown only where the type declares that capability. */
const CAPABILITY_ROWS: {
  capability: ConnectorCapability;
  labelKey: string;
  descriptionKey: string;
}[] = [
  {
    capability: "sms",
    labelKey: "connectors.form.smsLabel",
    descriptionKey: "connectors.form.smsDescription",
  },
  {
    capability: "voice",
    labelKey: "connectors.form.voiceLabel",
    descriptionKey: "connectors.form.voiceDescription",
  },
  {
    capability: "whatsapp",
    labelKey: "connectors.form.whatsappLabel",
    descriptionKey: "connectors.form.whatsappDescription",
  },
];

export const CapabilitiesField = ({
  type,
  capabilities,
  onToggle,
}: CapabilitiesFieldProps) => {
  const { t } = useTranslation("connectors");
  const available = CONNECTOR_CAPABILITIES[type];

  return (
    <div className="space-y-2">
      <Label>{t("connectors.form.capabilities")}</Label>
      <div className="space-y-2">
        {CAPABILITY_ROWS.filter((row) =>
          available.includes(row.capability),
        ).map((row) => (
          <div key={row.capability} className="flex items-center space-x-2">
            <Checkbox
              id={`capability-${row.capability}`}
              checked={capabilities.includes(row.capability)}
              onCheckedChange={() => onToggle(row.capability)}
            />
            <Label
              htmlFor={`capability-${row.capability}`}
              className="text-sm font-normal cursor-pointer"
            >
              {t(row.labelKey)} - {t(row.descriptionKey)}
            </Label>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {t("connectors.form.capabilitiesHint")}
      </p>
    </div>
  );
};
